import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../contexts/authcontext";
import { getActiveAnnouncement } from "../../services/announcementService";

const DEFAULT_BACKGROUND = "#1B4D47";
const DEFAULT_TEXT_COLOR = "#FFFFFF";
const DEFAULT_FONT_SIZE = 17;
const AUTO_ROTATE_INTERVAL_MS = 5000;

const AnnouncementBar = () => {
  const { token } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (token) {
      setAnnouncements([]);
      setCurrentIndex(0);
      return;
    }

    const fetchAnnouncements = async () => {
      const response = await getActiveAnnouncement();
      const activeList = (response?.data || []).filter(
        (item) => item?.announcementText
      );
      setAnnouncements(activeList);
      setCurrentIndex(0);
    };

    fetchAnnouncements();
  }, [token]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) =>
      announcements.length ? (prev + 1) % announcements.length : 0
    );
  }, [announcements.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) =>
      announcements.length
        ? (prev - 1 + announcements.length) % announcements.length
        : 0
    );
  }, [announcements.length]);

  useEffect(() => {
    if (announcements.length <= 1) {
      return undefined;
    }

    const timer = setInterval(goToNext, AUTO_ROTATE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [announcements.length, goToNext, currentIndex]);

  if (token || announcements.length === 0) {
    return null;
  }

  const current = announcements[currentIndex];
  const hasMultiple = announcements.length > 1;

  return (
    <div
      className={`site-announcement-bar${hasMultiple ? " site-announcement-bar--multiple" : ""}`}
      role="region"
      aria-label="Announcement"
      aria-live="polite"
      style={{
        backgroundColor: current.backgroundColor || DEFAULT_BACKGROUND,
        color: current.textColor || DEFAULT_TEXT_COLOR,
        fontSize: `${current.fontSize || DEFAULT_FONT_SIZE}px`,
      }}
    >
      <div className="site-announcement-bar__inner">
        <p className="site-announcement-bar__text">{current.announcementText}</p>
        {hasMultiple && (
          <>
            <button
              type="button"
              className="site-announcement-bar__prev"
              onClick={goToPrevious}
              aria-label="Previous announcement"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M15 6l-6 6 6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="site-announcement-bar__next"
              onClick={goToNext}
              aria-label="Next announcement"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M9 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AnnouncementBar;
